import { ThumbsDown, ThumbsUp } from "lucide-react";
import { ReactNode } from "react";
import { useImmer } from "use-immer";

import {
  IgDialog,
  IgIconButton,
  IgTextField,
  IgTypography,
} from "@intelligems/ig-theme";
import { AiFeature, SubmitAiFeedbackBody } from "@intelligems/ig-types";
import { Stack, useTheme } from "@mui/material";
import Grid from "@mui/material/Grid";
import { useSubmitAiFeedback } from "@src/api/ai/feedback/submit-feedback";
import { mpRateAi } from "@src/utils/mixpanel";

export type FeedbackTarget =
  | "mixpanel" // Only send to mixpanel
  | "ai"; // Send to mixpanel + ai.intelligems.io (web/ai)

interface RateAiProps {
  prependExtraButtons?: ReactNode;
  sessionId: string;
  feature: AiFeature;
  text?: ReactNode;
  metadata?: Record<string, string>;
  showCommentsDialog?: boolean;
  /** Initial rating value if feedback was already submitted */
  initialRating?: "good" | "bad" | null;
  /** Where to send feedback. Defaults to "mixpanel" */
  feedbackTarget?: FeedbackTarget;
  /** Required when feedbackTarget is "ai" */
  workflowId?: string;
  /** Optional additional payload fields for the "ai" target */
  aiFeedbackPayload?: Partial<
    Omit<SubmitAiFeedbackBody, "sessionId" | "workflowId" | "rating">
  >;
}

export type Rating = "good" | "bad";

export const RateAi = ({
  prependExtraButtons,
  sessionId,
  feature,
  text,
  showCommentsDialog = false,
  metadata = {},
  initialRating = null,
  feedbackTarget = "mixpanel",
  workflowId,
  aiFeedbackPayload,
}: RateAiProps) => {
  const theme = useTheme();
  const [rated, setRated] = useImmer<Rating | null>(initialRating);
  const [dialogOpen, setDialogOpen] = useImmer(false);
  const [pendingRating, setPendingRating] = useImmer<Rating | null>(null);
  const [comments, setComments] = useImmer("");

  const aiFeedback = useSubmitAiFeedback();

  const handleRatingSubmit = (
    rating: Rating,
    additionalComments?: string,
    metadata?: Record<string, string>
  ) => {
    if (feedbackTarget !== "ai") {
      mpRateAi(feature, rating, sessionId, additionalComments, metadata);
    }

    if (feedbackTarget === "ai" && workflowId) {
      aiFeedback.mutate(
        {
          sessionId,
          workflowId,
          rating,
          comment: additionalComments,
          ...aiFeedbackPayload,
        },
        {
          onSuccess: () => {
            setRated(rating);
            setComments("");
          },
        }
      );
      return;
    }

    setRated(rating);
  };

  const onClick = (rating: Rating) => {
    if (showCommentsDialog) {
      setPendingRating(rating);
      setDialogOpen(true);
    } else {
      handleRatingSubmit(rating);
    }
  };

  const handleDialogSubmit = () => {
    if (pendingRating) {
      handleRatingSubmit(pendingRating, comments, metadata);
      setDialogOpen(false);
      if (feedbackTarget !== "ai") {
        setComments("");
      }
      setPendingRating(null);
    }
  };

  const handleDialogClose = () => {
    // Submit rating even without comments
    if (pendingRating) {
      handleRatingSubmit(pendingRating, undefined, metadata);
    }
    setDialogOpen(false);
    setComments("");
    setPendingRating(null);
  };

  if (!sessionId) {
    return text ? <Grid>{text}</Grid> : null;
  }

  const buttons = (
    <Stack direction={"row"} spacing={0.5}>
      {prependExtraButtons}
      <IgIconButton
        size={"small"}
        aria-label="Rate helpful"
        onClick={() => onClick("good")}
        disabled={!!rated || aiFeedback.isPending}
      >
        <ThumbsUp
          size={14}
          color={rated === "good" ? theme.igIconColors.success : undefined}
        />
      </IgIconButton>
      <IgIconButton
        size={"small"}
        aria-label="Rate not helpful"
        onClick={() => onClick("bad")}
        disabled={!!rated || aiFeedback.isPending}
      >
        <ThumbsDown
          size={14}
          color={rated === "bad" ? theme.igIconColors.error : undefined}
        />
      </IgIconButton>
    </Stack>
  );

  const feedbackError =
    feedbackTarget === "ai" && aiFeedback.isError ? (
      <IgTypography variant="body2">
        Could not send rating. Try again.
      </IgTypography>
    ) : null;

  if (!text) {
    return (
      <>
        {buttons}
        {feedbackError}
        {showCommentsDialog && (
          <RateAiCommentDialog
            open={dialogOpen}
            rating={pendingRating}
            comments={comments}
            onCommentsChange={setComments}
            onSubmit={handleDialogSubmit}
            onSkip={handleDialogClose}
          />
        )}
      </>
    );
  }

  return (
    <>
      <Grid>
        {text}
        {buttons}
        {feedbackError}
      </Grid>
      {showCommentsDialog && (
        <RateAiCommentDialog
          open={dialogOpen}
          rating={pendingRating}
          comments={comments}
          onCommentsChange={setComments}
          onSubmit={handleDialogSubmit}
          onSkip={handleDialogClose}
        />
      )}
    </>
  );
};

interface RateAiCommentDialogProps {
  open: boolean;
  rating: Rating | null;
  comments: string;
  onCommentsChange: (comments: string) => void;
  onSubmit: () => void;
  /** Closing and "Skip" both land here: the rating stands without a comment. */
  onSkip: () => void;
}

/** The optional-comment step that follows a thumbs rating. */
export const RateAiCommentDialog = ({
  open,
  rating,
  comments,
  onCommentsChange,
  onSubmit,
  onSkip,
}: RateAiCommentDialogProps) => (
  <IgDialog
    showDialog={open}
    title="Thank you for your feedback!"
    handleClose={onSkip}
    handleYesClick={onSubmit}
    // `onSubmit` closes the dialog itself. Letting "Submit" also fire
    // `handleClose` would run `onSkip` too and record the rating twice.
    shouldNotClose
    handleNoClick={onSkip}
    yesButtonText="Submit"
    noButtonText="Skip"
    dialogContentWidth={"md"}
    buttonVariant="contained"
    content={
      <Grid container direction={"column"} spacing={2}>
        <Grid>
          <IgTypography>
            You rated this response as{" "}
            <IgTypography fontWeight={600}>
              {rating === "good" ? "helpful" : "not helpful"}
            </IgTypography>
            .
          </IgTypography>
        </Grid>
        <Grid>
          <IgTextField
            label="Additional Comments (optional)"
            multiline
            rows={4}
            fullWidth
            value={comments}
            onChange={(e) => onCommentsChange(e.target.value)}
            placeholder="Tell us more about your experience..."
          />
        </Grid>
      </Grid>
    }
  />
);
